'use client'

import { useState, useEffect } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  FolderOpen,
  Tag,
  ChevronRight,
  ChevronDown,
  Eye,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTicketGroups, useTicketGroupMutations } from '@/hooks/useTickets'
import { useToast } from '@/hooks/use-toast'
import type { TicketGroup, TicketCategory } from '@/types/ticket'

interface CategoryManagementModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CategoryManagementModal({
  open,
  onOpenChange,
}: CategoryManagementModalProps) {
  const { toast } = useToast()
  const { data: groups, refetch } = useTicketGroups()
  const {
    createGroup,
    updateGroup,
    deleteGroup,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useTicketGroupMutations()

  // View state
  const [activeView, setActiveView] = useState<'list' | 'tree'>('tree')

  // Tree view state
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())

  // Modal states
  const [groupModalOpen, setGroupModalOpen] = useState(false)
  const [categoryModalOpen, setCategoryModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')

  // Group form state
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null)
  const [groupName, setGroupName] = useState('')
  const [groupDescription, setGroupDescription] = useState('')

  // Category form state
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null
  )
  const [categoryName, setCategoryName] = useState('')
  const [categoryDescription, setCategoryDescription] = useState('')
  const [categoryGroupId, setCategoryGroupId] = useState<number | null>(null)

  const resetGroupForm = () => {
    setGroupModalOpen(false)
    setEditingGroupId(null)
    setGroupName('')
    setGroupDescription('')
  }

  const resetCategoryForm = () => {
    setCategoryModalOpen(false)
    setEditingCategoryId(null)
    setCategoryName('')
    setCategoryDescription('')
    setCategoryGroupId(null)
  }

  const openCreateGroupModal = () => {
    setModalMode('create')
    setEditingGroupId(null)
    setGroupName('')
    setGroupDescription('')
    setGroupModalOpen(true)
  }

  const openEditGroupModal = (group: TicketGroup) => {
    setModalMode('edit')
    setEditingGroupId(group.id)
    setGroupName(group.name)
    setGroupDescription(group.description || '')
    setGroupModalOpen(true)
  }

  const openCreateCategoryModal = () => {
    setModalMode('create')
    setEditingCategoryId(null)
    setCategoryName('')
    setCategoryDescription('')
    setCategoryGroupId(null)
    setCategoryModalOpen(true)
  }

  const openEditCategoryModal = (category: TicketCategory) => {
    setModalMode('edit')
    setEditingCategoryId(category.id)
    setCategoryName(category.name)
    setCategoryDescription(category.description || '')
    setCategoryGroupId(category.ticketGroupId)
    setCategoryModalOpen(true)
  }

  const handleSubmitGroup = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!groupName.trim()) {
      toast({
        title: 'Error',
        description: 'Group name is required',
        variant: 'destructive',
      })
      return
    }

    try {
      if (modalMode === 'create') {
        await createGroup.mutateAsync({
          name: groupName.trim(),
          description: groupDescription.trim() || undefined,
        })
        toast({
          title: 'Success',
          description: 'Group created successfully',
        })
      } else if (editingGroupId) {
        await updateGroup.mutateAsync({
          id: editingGroupId,
          data: {
            name: groupName.trim(),
            description: groupDescription.trim() || undefined,
          },
        })
        toast({
          title: 'Success',
          description: 'Group updated successfully',
        })
      }
      resetGroupForm()
      refetch()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save group',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteGroup = async (id: number) => {
    if (
      !confirm(
        'Are you sure you want to delete this group? All its categories will also be deleted.'
      )
    ) {
      return
    }

    try {
      await deleteGroup.mutateAsync(id)
      toast({
        title: 'Success',
        description: 'Group deleted successfully',
      })
      resetGroupForm()
      refetch()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete group',
        variant: 'destructive',
      })
    }
  }

  const handleSubmitCategory = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!categoryName.trim()) {
      toast({
        title: 'Error',
        description: 'Category name is required',
        variant: 'destructive',
      })
      return
    }

    if (!categoryGroupId) {
      toast({
        title: 'Error',
        description: 'Please select a group',
        variant: 'destructive',
      })
      return
    }

    try {
      if (modalMode === 'create') {
        await createCategory.mutateAsync({
          ticketGroupId: categoryGroupId,
          name: categoryName.trim(),
          description: categoryDescription.trim() || undefined,
        })
        toast({
          title: 'Success',
          description: 'Category created successfully',
        })
      } else if (editingCategoryId) {
        await updateCategory.mutateAsync({
          id: editingCategoryId,
          data: {
            ticketGroupId: categoryGroupId,
            name: categoryName.trim(),
            description: categoryDescription.trim() || undefined,
          },
        })
        toast({
          title: 'Success',
          description: 'Category updated successfully',
        })
      }
      resetCategoryForm()
      refetch()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save category',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteCategory = async (id: number) => {
    if (!confirm('Are you sure you want to delete this category?')) {
      return
    }

    try {
      await deleteCategory.mutateAsync(id)
      toast({
        title: 'Success',
        description: 'Category deleted successfully',
      })
      resetCategoryForm()
      refetch()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete category',
        variant: 'destructive',
      })
    }
  }

  // Get all categories across groups
  const allCategories =
    groups?.flatMap((g: TicketGroup) => g.categories || []) || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Groups & Categories</DialogTitle>
          <DialogDescription>
            Create and organize ticket groups and categories
          </DialogDescription>
        </DialogHeader>

        {/* View Toggle */}
        <div className="flex justify-end gap-2 mb-4 mt-4">
          {/*
          <Button
            variant={activeView === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveView('list')}
            className="gap-2"
          >
            <List className="h-4 w-4" />
            List View
          </Button>
          */}
          <Button
            variant={activeView === 'tree' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveView('tree')}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            Tree View
          </Button>
        </div>

        {/* List View - Contains Groups and Categories sub-tabs (hidden for now) */}
        {/*
        {activeView === 'list' && (
          <Tabs defaultValue="groups" className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="groups">
                <FolderOpen className="h-4 w-4 mr-2" />
                Groups
              </TabsTrigger>
              <TabsTrigger value="categories">
                <Tag className="h-4 w-4 mr-2" />
                Categories
              </TabsTrigger>
            </TabsList>

            <TabsContent value="groups" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Groups</h3>
                <Button onClick={openCreateGroupModal} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Group
                </Button>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold">Existing Groups</h3>
                {groups && groups.length > 0 ? (
                  groups.map((group: TicketGroup) => (
                    <div key={group.id} className="flex items-start justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{group.name}</p>
                        {group.description && (
                          <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {group.categories?.length || 0} categories
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditGroup(group)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteGroup(group.id)}
                          disabled={deleteGroup.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No groups yet</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="categories" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Categories</h3>
                <Button onClick={openCreateCategoryModal} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Category
                </Button>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold">Existing Categories</h3>
                {allCategories && allCategories.length > 0 ? (
                  allCategories.map((category: TicketCategory) => {
                    const group = groups?.find((g: TicketGroup) => g.id === category.ticketGroupId)
                    return (
                      <div key={category.id} className="flex items-start justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">{category.name}</p>
                          {category.description && (
                            <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Group: {group?.name || 'Unknown'}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditGroupModal(group)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id)}
                            disabled={deleteCategory.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No categories yet</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
        */}

        {/* Tree View - Shows hierarchy with edit/delete capabilities */}
        {activeView === 'tree' && (
          <div className="p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Hierarchical View</h3>
              <div className="flex items-center gap-2">
                <Button
                  onClick={openCreateGroupModal}
                  size="sm"
                  variant="outline"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Group
                </Button>
                <Button
                  onClick={openCreateCategoryModal}
                  size="sm"
                  variant="outline"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Category
                </Button>
              </div>
            </div>

            {!groups || groups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No groups created yet</p>
                <p className="text-xs mt-1">
                  Create your first group to get started
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {groups.map((group: TicketGroup) => {
                  const isExpanded = expandedGroups.has(group.id)
                  const categoryCount = group.categories?.length || 0

                  return (
                    <div
                      key={group.id}
                      className="border rounded-lg bg-white overflow-hidden"
                    >
                      {/* Group Row */}
                      <div
                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setExpandedGroups(prev => {
                            const next = new Set(prev)
                            if (next.has(group.id)) {
                              next.delete(group.id)
                            } else {
                              next.add(group.id)
                            }
                            return next
                          })
                        }}
                      >
                        <div className="flex items-center flex-1">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 mr-2" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 mr-2" />
                          )}
                          <FolderOpen className="h-4 w-4 text-blue-600 mr-2" />
                          <div>
                            <div className="font-medium text-gray-900">
                              {group.name}
                            </div>
                            {group.description && (
                              <div className="text-xs text-gray-500">
                                {group.description}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {categoryCount}{' '}
                            {categoryCount === 1 ? 'category' : 'categories'}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={e => {
                              e.stopPropagation()
                              openEditGroupModal(group)
                            }}
                            title="Edit group"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={e => {
                              e.stopPropagation()
                              handleDeleteGroup(group.id)
                            }}
                            title="Delete group"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {/* Categories List (Expanded) */}
                      {isExpanded && (
                        <div className="border-t bg-gray-50">
                          {categoryCount === 0 ? (
                            <div className="p-4 text-center text-sm text-gray-500">
                              No categories in this group yet
                            </div>
                          ) : (
                            <div className="divide-y">
                              {group.categories?.map(
                                (category: TicketCategory) => (
                                  <div
                                    key={category.id}
                                    className="flex items-center justify-between p-3 pl-12 hover:bg-gray-100"
                                  >
                                    <div className="flex items-center flex-1">
                                      <Tag className="h-3 w-3 text-green-600 mr-2" />
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {category.name}
                                        </div>
                                        {category.description && (
                                          <div className="text-xs text-gray-500">
                                            {category.description}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                          openEditCategoryModal(category)
                                        }
                                        title="Edit category"
                                      >
                                        <Pencil className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                          handleDeleteCategory(category.id)
                                        }
                                        title="Delete category"
                                      >
                                        <Trash2 className="h-3 w-3 text-red-500" />
                                      </Button>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-900">
                <strong>Tip:</strong> Click on a group to expand and see its
                categories. Click edit buttons to modify items directly.
              </p>
            </div>
          </div>
        )}

        {/* Group Modal */}
        <Dialog open={groupModalOpen} onOpenChange={setGroupModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {modalMode === 'create' ? 'Create New Group' : 'Edit Group'}
              </DialogTitle>
              <DialogDescription>
                Groups help organize tickets into major categories
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitGroup} className="space-y-4">
              <div>
                <Label htmlFor="groupName">Group Name *</Label>
                <Input
                  id="groupName"
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="e.g., Technical Support"
                />
              </div>
              <div>
                <Label htmlFor="groupDescription">Description</Label>
                <Textarea
                  id="groupDescription"
                  value={groupDescription}
                  onChange={e => setGroupDescription(e.target.value)}
                  placeholder="Brief description of this group"
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetGroupForm}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createGroup.isPending || updateGroup.isPending}
                >
                  {modalMode === 'create' ? 'Create Group' : 'Update Group'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Category Modal */}
        <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {modalMode === 'create'
                  ? 'Create New Category'
                  : 'Edit Category'}
              </DialogTitle>
              <DialogDescription>
                Categories are specific types within a group
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmitCategory} className="space-y-4">
              <div>
                <Label htmlFor="categoryGroup">Group *</Label>
                <Select
                  value={categoryGroupId?.toString() || ''}
                  onValueChange={value => setCategoryGroupId(parseInt(value))}
                >
                  <SelectTrigger id="categoryGroup">
                    <SelectValue placeholder="Select a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups &&
                      groups.map((group: TicketGroup) => (
                        <SelectItem key={group.id} value={group.id.toString()}>
                          {group.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="categoryName">Category Name *</Label>
                <Input
                  id="categoryName"
                  value={categoryName}
                  onChange={e => setCategoryName(e.target.value)}
                  placeholder="e.g., Login Issues"
                />
              </div>
              <div>
                <Label htmlFor="categoryDescription">Description</Label>
                <Textarea
                  id="categoryDescription"
                  value={categoryDescription}
                  onChange={e => setCategoryDescription(e.target.value)}
                  placeholder="Brief description of this category"
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetCategoryForm}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    createCategory.isPending || updateCategory.isPending
                  }
                >
                  {modalMode === 'create'
                    ? 'Create Category'
                    : 'Update Category'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}
